import { resolveValue } from 'components/form/core/hooks/useField'
import { Calculation } from 'final-form-calculate'
import { Cid10, SituacaoProblema } from 'graphql/types.generated'
import { emptyArrayOf } from 'util/array'
import { isUndefinedOrNull } from 'util/checks'
import { MetaPath } from 'util/metaPath'
import { CiapCidPreNatal, SoapState } from 'view/atendimentos/atendimento-individual/model'
import { RegistroAvaliacaoPuericultura } from 'view/atendimentos/model-atendimento'

import { Problema } from '../aside/types/ProblemaModel'
import { ObjetivoFormModel } from '../objetivo'
import { FormAtivoObjetivoEnum } from '../objetivo/model'
import { createPreNatalAvaliacaoCalculations } from '../pre-natal/calculator-preNatal'
import { AvaliacaoFormModel } from './AvaliacaoForm'
import { ProblemaCondicaoModel } from './components/problemas-condicoes/model-problemasCondicoes'
import {
  addProblemaCondicaoAutomatico,
  createProblemaCondicaoAutomatico,
  createProblemaCondicaoAutomaticoPreNatal,
  findProblemaByCiapIdCidId,
  removeProblemaCondicao,
  removeProblemasCondicoesDePreNatal,
} from './components/problemas-condicoes/utils/operations-problemasCondicoes'
import {
  hasProblemaCondicaoDePreNatal,
  isProblemaComCiapW78Ativo,
} from './components/problemas-condicoes/utils/verifications-preNatal'

export const createAvaliacaoCalculations = (
  objetivo: MetaPath<ObjetivoFormModel>,
  avaliacao: MetaPath<AvaliacaoFormModel>,
  problemaCondicaoPuericultura: RegistroAvaliacaoPuericultura,
  ciapCidPreNatal: CiapCidPreNatal,
  dataNascimentoCidadao: LocalDate,
  dataAtendimento: Instant,
  hasPermissionPreNatal: boolean,
  isContinuacaoPreNatal: boolean,
  hasProblemaComCiapW78AtivoPersistido: boolean,
  problemasCidadao?: Problema[]
): Calculation[] => [
  ...createPreNatalAvaliacaoCalculations(
    avaliacao,
    dataNascimentoCidadao,
    dataAtendimento,
    ciapCidPreNatal,
    isContinuacaoPreNatal,
    hasPermissionPreNatal,
    problemasCidadao
  ),
  {
    field: objetivo.atendimentosEspecificos.formAtivo.absolutePath(),
    updates: {
      [avaliacao.problemasECondicoes.absolutePath()]: (
        formAtivo: FormAtivoObjetivoEnum,
        allValues: SoapState,
        prevValues: SoapState
      ) => {
        let listaProblemasCondicoes =
          resolveValue<ProblemaCondicaoModel[]>(allValues, avaliacao.problemasECondicoes) ??
          emptyArrayOf<ProblemaCondicaoModel>()
        const formEstavaAtivo = resolveValue<FormAtivoObjetivoEnum>(
          prevValues,
          objetivo.atendimentosEspecificos.formAtivo
        )

        let cidPrimeiroAtendimentoPreNatal = null
        if (formAtivo === FormAtivoObjetivoEnum.PRE_NATAL || formEstavaAtivo === FormAtivoObjetivoEnum.PRE_NATAL)
          cidPrimeiroAtendimentoPreNatal = problemasCidadao.find(isProblemaComCiapW78Ativo).cid10

        if (!isUndefinedOrNull(formAtivo)) {
          listaProblemasCondicoes = handleFormAtivo(
            formAtivo,
            listaProblemasCondicoes,
            problemaCondicaoPuericultura,
            cidPrimeiroAtendimentoPreNatal,
            ciapCidPreNatal,
            dataNascimentoCidadao,
            hasProblemaComCiapW78AtivoPersistido,
            problemasCidadao
          )
        }

        if (!isUndefinedOrNull(formEstavaAtivo)) {
          listaProblemasCondicoes = handleFormEstavaAtivo(
            formEstavaAtivo,
            problemaCondicaoPuericultura,
            listaProblemasCondicoes,
            hasProblemaComCiapW78AtivoPersistido
          )
        }

        return listaProblemasCondicoes
      },
    },
  },
]

const handleFormEstavaAtivo = (
  formEstavaAtivo: FormAtivoObjetivoEnum,
  problemaCondicaoPuericultura: RegistroAvaliacaoPuericultura,
  listaProblemasCondicoes: readonly ProblemaCondicaoModel[],
  hasProblemaComCiapW78AtivoPersistido: boolean
): readonly ProblemaCondicaoModel[] => {
  switch (formEstavaAtivo) {
    case FormAtivoObjetivoEnum.PUERICULTURA: {
      return removeProblemaCondicao(listaProblemasCondicoes, {
        ciapToRemove: problemaCondicaoPuericultura.ciap?.codigo,
        cidToRemove: problemaCondicaoPuericultura.cid10?.codigo,
      })
    }
    case FormAtivoObjetivoEnum.PRE_NATAL: {
      return removeProblemasCondicoesDePreNatal(listaProblemasCondicoes, hasProblemaComCiapW78AtivoPersistido)
    }
    default:
      return listaProblemasCondicoes
  }
}

const handleFormAtivo = (
  formAtivo: FormAtivoObjetivoEnum,
  listaProblemasCondicoes: readonly ProblemaCondicaoModel[],
  problemaCondicaoPuericultura: RegistroAvaliacaoPuericultura,
  cidPrimeiroAtendimento: Cid10,
  ciapCidPreNatal: CiapCidPreNatal,
  dataNascimentoCidadao: LocalDate,
  hasProblemaComCiapW78AtivoPersistido: boolean,
  problemasCidadao?: Problema[]
): readonly ProblemaCondicaoModel[] => {
  switch (formAtivo) {
    case FormAtivoObjetivoEnum.PRE_NATAL: {
      if (hasProblemaCondicaoDePreNatal(listaProblemasCondicoes, hasProblemaComCiapW78AtivoPersistido)) {
        return listaProblemasCondicoes
      } else {
        const problemaPreNatalJaExistente = findProblemaByCiapIdCidId(
          problemasCidadao,
          ciapCidPreNatal.ciap?.id,
          cidPrimeiroAtendimento?.id
        )
        return addProblemaCondicaoAutomatico(
          listaProblemasCondicoes,
          createProblemaCondicaoAutomaticoPreNatal(
            ciapCidPreNatal,
            SituacaoProblema.ATIVO,
            cidPrimeiroAtendimento,
            problemaPreNatalJaExistente,
            dataNascimentoCidadao
          )
        )
      }
    }
    case FormAtivoObjetivoEnum.PUERICULTURA: {
      return addProblemaCondicaoAutomatico(
        listaProblemasCondicoes,
        createProblemaCondicaoAutomaticoPuericultura(problemaCondicaoPuericultura)
      )
    }
    default:
      return listaProblemasCondicoes
  }
}

const createProblemaCondicaoAutomaticoPuericultura = (
  problemaCondicaoPuericultura: RegistroAvaliacaoPuericultura
): ProblemaCondicaoModel =>
  createProblemaCondicaoAutomatico(problemaCondicaoPuericultura.ciap, problemaCondicaoPuericultura.cid10)
